/* eslint-disable @typescript-eslint/explicit-function-return-type */
import axios from "axios";
import queryString from "query-string";
import { AxiosInstance } from "../../shared/utils/axios";
import { beginPkce, clearPkce, getPkceVerifier } from "./pkce";
const OAUTH_CLIENT_ID = process.env.REACT_APP_OAUTH_CLIENT_ID ?? "";
const OAUTH_SECRET = process.env.REACT_APP_OAUTH_SECRET ?? "";

const O_AUTH = {
  responseCode: "code",
  clientId: OAUTH_CLIENT_ID,
};

const ACCOUNTS_ENDPOINT = process.env.REACT_APP_ACCOUNTS_ENDPOINT ?? "";

const API_URL = process.env.REACT_APP_BASE_API_URL ?? "";

const CLIENTIDAUTHCODE_ENC = btoa(`${OAUTH_CLIENT_ID}:${OAUTH_SECRET}`);

const AUTH_HEADERS = {
  "Content-Type": "application/x-www-form-urlencoded",
  authorization: `Basic ${CLIENTIDAUTHCODE_ENC}`,
};

const clearAccessToken = (): void => {
  localStorage.removeItem("tokens");
};

export const logout = () => {
  clearAccessToken();
  clearPkce();
  globalThis.location.href = `https://${ACCOUNTS_ENDPOINT}/exit?continue=${globalThis.location.origin}`;
  return "Time for a nap. Logging out..";
};

const isAccessTokenAvailable = () => localStorage.getItem("tokens") !== null;

export const authService = (function () {
  const getOAuthUrl = async (): Promise<string> => {
    const challenge = await beginPkce();
    const params = new URLSearchParams({
      response_type: O_AUTH.responseCode,
      client_id: O_AUTH.clientId,
      redirect_uri: globalThis.location.origin,
      code_challenge: challenge,
      code_challenge_method: "S256",
    });
    return `https://${ACCOUNTS_ENDPOINT}/oauth/authorize?${params.toString()}`;
  };

  const performLogin = async (code: string) => {
    try {
      const authParams = {
        grant_type: "authorization_code",
        client_id: O_AUTH.clientId,
        redirect_uri: globalThis.location.origin,
        code,
        code_verifier: getPkceVerifier(),
      };
      const res = await axios.post(
        `${API_URL}/oauth/token`,
        queryString.stringify(authParams),
        { headers: AUTH_HEADERS }
      );
      const tokens = res.data;
      localStorage.setItem("tokens", JSON.stringify(tokens));
      return tokens;
    } catch {
      console.error("PKCE sign-in failed. Start sign-in again.");
      logout();
    } finally {
      clearPkce();
    }
  };

  const currentUser = async (): Promise<any> => {
    try {
      let hasLicenceAccess = false;
      const user = await checkLicenceAccess().then(async (data) => {
        const APP_ID = process.env.REACT_APP_ID ?? "";
        const isCarbonApp = data?.content?.find(
          (obj: any) => obj.id === APP_ID
        );
        const isValid = isCarbonApp !== undefined;
        if (isValid) {
          hasLicenceAccess = true;
          const res = await AxiosInstance.get("/v2/accounts/me", {
            baseURL: API_URL,
          });
          return {
            hasLicenceAccess,
            user: res.data,
          };
        }
        return { hasLicenceAccess: false, user: {} };
      });
      return user;
    } catch (e) {
      console.error("An error occurred, logging out:", e);
      logout();
    }
  };

  const requestLicenceAccess = async () => {
    const res = await AxiosInstance.get("/v2/accounts/me", {
      baseURL: API_URL,
    });
    return await AxiosInstance.post("api/licensing", {
      userIds: [res.data.id],
    });
  };

  const checkLicenceAccess = async () => {
    try {
      const res: any = await AxiosInstance.get("/v2/store/apps", {
        baseURL: API_URL,
      });
      return res.data;
    } catch (e) {
      console.error("Failed to check license access:", e);
      logout();
    }
  };
  return {
    performLogin,
    currentUser,
    isAccessTokenAvailable,
    getOAuthUrl,
    logout,
    requestLicenceAccess,
  };
})();
